import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../store/modules/rootReducer'
import { useTimerData } from '../providers/TimerDataProvider'
import {
  startTimer as startTimerAction,
  pauseTimer as pauseTimerAction,
  resumeTimer as resumeTimerAction,
  stopTimer as stopTimerAction,
  resetTimer as resetTimerAction,
  restartTimer as restartTimerAction,
  addTime as addTimeAction,
  toggleMode as toggleModeAction,
  setVisibility as setVisibilityAction,
} from '../store/modules/timer/actions'

export interface ITimerSession {
  id: string
  subject: string
  duration: number // em segundos
  color?: string
}

export interface IUseTimerReturn {
  // Estado
  status: 'idle' | 'running' | 'paused' | 'stopped'
  mode: 'card' | 'fullscreen'
  timeRemaining: number // em segundos
  progress: number // 0-100
  isVisible: boolean

  // Dados da sessão atual
  currentSession: ITimerSession | null

  // Tempo formatado
  formattedTime: string

  // Tempo extra adicionado
  addedTime: number // em segundos

  // Actions
  startTimer: (sessionId?: string, duration?: number) => void
  pauseTimer: () => void
  resumeTimer: () => void
  stopTimer: () => void
  resetTimer: () => void
  restartTimer: () => void
  addTime: (seconds: number) => void
  toggleMode: () => void
  setVisibility: (visible: boolean) => void

  // Tempo de pausa
  pauseElapsed: number
}

/**
 * Hook customizado para gerenciar o cronômetro de estudos
 * Integra com o Redux para estado persistente e com o ciclo atual do planejador
 */
export const useTimer = (): IUseTimerReturn => {
  const dispatch = useDispatch()

  // Estado do Redux
  const timerState = useSelector((state: RootState) => state.timer)

  // Busca sessões do ciclo atual do contexto compartilhado (uma única instância)
  const { cycleData } = useTimerData()

  // Estado local para forçar re-render a cada segundo
  const [tick, setTick] = useState(0)

  // Identifica sessão atual (primeira não finalizada)
  const currentCycleSession = useMemo(() => {
    if (!cycleData?.sessions) return null
    return cycleData.sessions.find((s) => s.status !== 'done')
  }, [cycleData])

  // Monta objeto da sessão atual
  const currentSession = useMemo<ITimerSession | null>(() => {
    if (!currentCycleSession) return null

    return {
      id: currentCycleSession._id,
      subject: currentCycleSession.module?.subject || 'Sem disciplina',
      duration: Math.floor(currentCycleSession.hours * 3600), // horas para segundos
      color: currentCycleSession.color || '#027A00',
    }
  }, [currentCycleSession])

  // Calcula tempo restante baseado em timestamps
  const calculateTimeRemaining = useCallback((): number => {
    const { status, startTimestamp, pauseDuration, initialDuration, addedTime, pauseTimestamp } =
      timerState

    // Se parado (finalizado), retorna 0
    if (status === 'stopped') {
      return 0
    }

    // Se idle (não iniciado), retorna duração inicial
    if (status === 'idle') {
      return initialDuration + addedTime
    }

    if (status === 'paused' && pauseTimestamp && startTimestamp) {
      // Quando pausado, calcula baseado no momento do pause
      const elapsed =
        Math.floor((pauseTimestamp - startTimestamp) / 1000) - Math.floor(pauseDuration / 1000)
      const total = initialDuration + addedTime
      return Math.max(0, total - elapsed)
    }

    if (status === 'running' && startTimestamp) {
      const now = Date.now()
      const elapsed = Math.floor((now - startTimestamp) / 1000) - Math.floor(pauseDuration / 1000)
      const total = initialDuration + addedTime
      return Math.max(0, total - elapsed)
    }

    return initialDuration + addedTime
  }, [timerState])

  // Calcula progresso (0-100)
  const calculateProgress = useCallback((): number => {
    const { initialDuration, addedTime } = timerState
    const total = initialDuration + addedTime

    if (total === 0) return 0

    const remaining = calculateTimeRemaining()
    const elapsed = total - remaining
    return Math.min(100, Math.max(0, (elapsed / total) * 100))
  }, [timerState, calculateTimeRemaining])

  // Calcula tempo de pausa decorrido (acumulativo)
  const calculatePauseElapsed = useCallback((): number => {
    const { status, pauseStartTimestamp, pauseDuration } = timerState

    if (status === 'paused' && pauseStartTimestamp) {
      // Tempo total de pausa = pausas anteriores + pausa atual
      const currentPauseTime = Date.now() - pauseStartTimestamp
      const totalPauseTime = pauseDuration + currentPauseTime
      return Math.floor(totalPauseTime / 1000)
    }

    // Se não está pausado mas já pausou antes, retorna o acumulado
    if (pauseDuration > 0) {
      return Math.floor(pauseDuration / 1000)
    }

    return 0
  }, [timerState])

  // Formata tempo em HH:MM:SS ou MM:SS
  const formatTime = useCallback((seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs
        .toString()
        .padStart(2, '0')}`
    }

    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }, [])

  // Atualiza a cada segundo quando rodando
  useEffect(() => {
    if (timerState.status === 'running' || timerState.status === 'paused') {
      const interval = setInterval(() => {
        setTick((prev) => prev + 1)
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [timerState.status])

  // Verifica se o tempo expirou
  useEffect(() => {
    if (timerState.status === 'running') {
      const remaining = calculateTimeRemaining()

      if (remaining <= 0) {
        // Tempo expirou - para automaticamente
        dispatch(stopTimerAction())
      }
    }
  }, [tick, timerState.status, calculateTimeRemaining, dispatch])

  // Actions
  const startTimer = useCallback(
    (sessionId?: string, duration?: number) => {
      // Se não passar sessionId/duration, usa da sessão atual
      const id = sessionId || currentSession?.id
      const dur = duration || currentSession?.duration

      if (id && dur) {
        dispatch(startTimerAction(id, dur))
      }
    },
    [dispatch, currentSession]
  )

  const pauseTimer = useCallback(() => {
    dispatch(pauseTimerAction())
  }, [dispatch])

  const resumeTimer = useCallback(() => {
    dispatch(resumeTimerAction())
  }, [dispatch])

  const stopTimer = useCallback(() => {
    dispatch(stopTimerAction())
  }, [dispatch])

  const resetTimer = useCallback(() => {
    dispatch(resetTimerAction())
  }, [dispatch])

  const restartTimer = useCallback(() => {
    dispatch(restartTimerAction())
  }, [dispatch])

  const addTime = useCallback(
    (seconds: number) => {
      dispatch(addTimeAction(seconds))
    },
    [dispatch]
  )

  const toggleMode = useCallback(() => {
    dispatch(toggleModeAction())
  }, [dispatch])

  const setVisibility = useCallback(
    (visible: boolean) => {
      dispatch(setVisibilityAction(visible))
    },
    [dispatch]
  )

  // Valores calculados
  const timeRemaining = calculateTimeRemaining()
  const progress = calculateProgress()
  const pauseElapsed = calculatePauseElapsed()
  const formattedTime = formatTime(timeRemaining)

  return {
    // Estado
    status: timerState.status,
    mode: timerState.mode,
    timeRemaining,
    progress,
    isVisible: timerState.isVisible,

    // Sessão atual
    currentSession,

    // Tempo formatado
    formattedTime,

    // Tempo extra adicionado
    addedTime: timerState.addedTime,

    // Actions
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    resetTimer,
    restartTimer,
    addTime,
    toggleMode,
    setVisibility,

    // Pausa
    pauseElapsed,
  }
}
